import { useEffect, useRef, type PointerEvent } from 'react';
import type { GameControls } from '../hooks/useGame';

interface TouchControlsProps {
  controls: GameControls;
  paused: boolean;
  holdDisabled?: boolean;
  haptics?: boolean;
}

export function TouchControls({ controls, paused, holdDisabled = false, haptics = true }: TouchControlsProps) {
  const repeatTimer = useRef<number | null>(null);
  const repeatDelay = useRef<number | null>(null);

  const stopRepeat = () => {
    if (repeatTimer.current !== null) window.clearInterval(repeatTimer.current);
    if (repeatDelay.current !== null) window.clearTimeout(repeatDelay.current);
    repeatTimer.current = null;
    repeatDelay.current = null;
  };

  useEffect(() => stopRepeat, []);

  const beginRepeat = (event: PointerEvent<HTMLButtonElement>, action: () => void) => {
    event.preventDefault();
    if (haptics) navigator.vibrate?.(8);
    action();
    repeatDelay.current = window.setTimeout(() => {
      repeatTimer.current = window.setInterval(action, 70);
    }, 170);
  };

  const runOnce = (action: () => void, duration = 10) => {
    if (haptics) navigator.vibrate?.(duration);
    action();
  };

  return (
    <nav className="touch-controls" aria-label="Touch game controls" onContextMenu={(event) => event.preventDefault()}>
      <div className="touch-controls__movement">
        <button type="button" className="touch-button" aria-label="Move left" onPointerDown={(event) => beginRepeat(event, controls.moveLeft)} onPointerUp={stopRepeat} onPointerCancel={stopRepeat}>←</button>
        <button type="button" className="touch-button" aria-label="Soft drop" onPointerDown={(event) => beginRepeat(event, controls.softDrop)} onPointerUp={stopRepeat} onPointerCancel={stopRepeat}>↓</button>
        <button type="button" className="touch-button" aria-label="Move right" onPointerDown={(event) => beginRepeat(event, controls.moveRight)} onPointerUp={stopRepeat} onPointerCancel={stopRepeat}>→</button>
      </div>
      <div className="touch-controls__actions">
        <button type="button" className="touch-button touch-button--small" aria-label="Hold piece" disabled={holdDisabled} onClick={() => runOnce(controls.hold)}>HOLD</button>
        <button type="button" className="touch-button touch-button--rotate" aria-label="Rotate clockwise" onClick={() => runOnce(controls.rotateClockwise)}>↻</button>
        <button type="button" className="touch-button touch-button--drop" aria-label="Hard drop" onClick={() => runOnce(controls.hardDrop, 14)}>DROP</button>
        <button type="button" className="touch-button touch-button--small" aria-label={paused ? 'Resume game' : 'Pause game'} onClick={() => runOnce(controls.togglePause)}>{paused ? 'PLAY' : 'PAUSE'}</button>
      </div>
    </nav>
  );
}
